import type { GuideInline } from "./guide-text";
import type { LeaguePartStyle } from "./league-page";
import type { PageNode, PageTextLink } from "./pages";
import { isPageColor, pageFont, pageIconMask } from "./page-design-runtime";

const backgrounds = {
  none: "transparent",
  page: "var(--bg, var(--gaming-bg, var(--paper)))",
  surface: "var(--paper)",
  tint: "var(--tint, var(--paper))",
  muted: "color-mix(in srgb, var(--muted) 8%, var(--paper))",
  accent: "var(--accent)",
  card: "var(--page-card-color, var(--paper))",
  shade:
    "linear-gradient(to top, var(--bg, var(--gaming-bg, var(--paper))), color-mix(in srgb, var(--bg, var(--gaming-bg, var(--paper))) 25%, transparent) 45%, transparent)",
  // Laid over a picture behind text: the page color holds the reading side and the foot, and
  // the picture shows through, dimmed, toward the far edge.
  // The primary color's own ink: a button's fill over a photo, as storefront heroes invert it.
  inverse: "var(--on-accent)",
  ink: "var(--ink, var(--gaming-text, inherit))",
  // A photo needs dark contrast even on a light page; independent of the creator palette.
  scrim:
    "linear-gradient(90deg, color-mix(in srgb, var(--ui-media-shade, black) 64%, transparent), color-mix(in srgb, var(--ui-media-shade, black) 12%, transparent) 75%)",
  // The page color across the reading side, clearing toward the far edge so a picture there
  // shows at full strength, as a matchup header lays its words beside champion art.
  fade: "linear-gradient(90deg, var(--bg, var(--gaming-bg, var(--paper))) 0%, color-mix(in srgb, var(--bg, var(--gaming-bg, var(--paper))) 88%, transparent) 38%, transparent 78%)",
  // The page color at 80% over a picture, as a split hero washes its background photo.
  wash: "color-mix(in srgb, var(--bg, var(--gaming-bg, var(--paper))) 80%, transparent)",
  veil: "linear-gradient(to bottom, transparent, color-mix(in srgb, var(--bg, var(--gaming-bg, var(--paper))) 35%, transparent) 50%, var(--bg, var(--gaming-bg, var(--paper)))), linear-gradient(to right, var(--bg, var(--gaming-bg, var(--paper))), color-mix(in srgb, var(--bg, var(--gaming-bg, var(--paper))) 90%, transparent) 50%, color-mix(in srgb, var(--bg, var(--gaming-bg, var(--paper))) 66%, transparent))",
};
const colors = {
  text: "var(--ink, var(--gaming-text, inherit))",
  muted: "var(--muted)",
  accent: "var(--accent-ink, var(--accent))",
  inverse: "var(--on-accent)",
  page: "var(--bg, var(--gaming-bg, var(--paper)))",
  card: "var(--page-card-ink, var(--ink, inherit))",
};
const gradientColors = {
  page: backgrounds.page,
  surface: backgrounds.surface,
  tint: backgrounds.tint,
  accent: backgrounds.accent,
  text: colors.text,
  muted: colors.muted,
  inverse: colors.inverse,
  line: "var(--line)",
};
/** A theme color by name, or the creator's own checked "#rrggbb"; anything else is left unset. */
function tokenOrColor(value: string | undefined, tokens: Record<string, string>) {
  if (!value) return undefined;
  if (Object.hasOwn(tokens, value)) return tokens[value];
  return isPageColor(value) ? value : undefined;
}
/**
 * A League card's heading, label or tag in the type its part style sets, over the theme's. A
 * size of 32px or more narrows with a phone's screen, as large text elements do.
 */
export function leaguePartStyle(style: LeaguePartStyle | undefined): Record<string, string> {
  if (!style) return {};
  const size = style.fontSize;
  const entries = {
    fontFamily: style.font ? pageFont(style.font) : undefined,
    fontSize:
      size === undefined ? undefined : size >= 32 ? `clamp(28px, 7vw, ${size}px)` : `${size}px`,
    fontWeight: style.fontWeight === undefined ? undefined : String(style.fontWeight),
    fontStyle: style.fontStyle,
    letterSpacing: style.letterSpacing === undefined ? undefined : `${style.letterSpacing}em`,
    textTransform:
      style.uppercase === undefined ? undefined : style.uppercase ? "uppercase" : "none",
    color: tokenOrColor(style.color, colors),
  };
  return Object.fromEntries(
    Object.entries(entries).filter((entry): entry is [string, string] => entry[1] !== undefined),
  );
}
const alignments = { start: "flex-start", center: "center", end: "flex-end" };
const textAlignments = { start: "left", center: "center", end: "right" };
const dimension = (value: number | undefined, fallback = "initial") =>
  typeof value === "number" && Number.isFinite(value) ? `${value}px` : fallback;

/** The shapes a picture, video or frame can be cut to, which scale with the element. */
export const pageShapeRadius = {
  leaf: "30% 10%",
  "leaf-reverse": "10% 30%",
  blob: "60% 40% 30% 70% / 60% 30% 70% 40%",
  pebble: "42% 58% 70% 30% / 45% 45% 55% 55%",
  petal: "50% 0",
  drop: "50% 50% 50% 0",
} as const;

const corners = {
  card: "var(--creator-card-radius, 16px)",
  image: "var(--creator-image-radius, 12px)",
  control: "var(--creator-button-radius, 8px)",
  // Corners larger than the element shrink to fit it: a circle, a pill, or a half-circle top.
  round: "9999px",
  arch: "9999px 9999px 0 0",
  ...pageShapeRadius,
};

/** The foreground follows a repeated card's selected semantic background. */
function cardInk(background: string) {
  if (background === "accent") return "var(--on-accent)";
  if (background === "inverse") return "var(--accent)";
  if (background === "ink") return "var(--bg, var(--gaming-bg, var(--paper)))";
  return "var(--ink, inherit)";
}

/** A named region carries its own readable foreground; an element may still override it. */
const regionInk: Record<string, string> = {
  page: colors.text,
  surface: colors.text,
  tint: colors.text,
  muted: colors.text,
  accent: colors.inverse,
  ink: colors.page,
  inverse: backgrounds.accent,
  card: colors.card,
};

/**
 * The words a text accents. Several are separated by commas ("Builds, Every mechanic"); words
 * the text holds whole, commas and all, stay one accent, as a single accent always has.
 */
function pageAccentPhrases(highlight: string | undefined, text: string) {
  if (!highlight?.trim()) return [];
  if (text.includes(highlight)) return [highlight];
  return [...new Set(highlight.split(",").map((phrase) => phrase.trim()))].filter(Boolean);
}

/** Each phrase's first appearance in a text, as plain pieces, and the phrases that appeared. */
function accentPieces(text: string, phrases: readonly string[]) {
  const ranges: { start: number; end: number }[] = [];
  const used: string[] = [];
  for (const phrase of phrases) {
    const start = text.indexOf(phrase);
    const end = start + phrase.length;
    if (start < 0 || ranges.some((range) => start < range.end && end > range.start)) continue;
    ranges.push({ start, end });
    used.push(phrase);
  }
  ranges.sort((first, second) => first.start - second.start);
  const parts: { text: string; accent: boolean }[] = [];
  let at = 0;
  for (const range of ranges) {
    parts.push(
      { text: text.slice(at, range.start), accent: false },
      { text: text.slice(range.start, range.end), accent: true },
    );
    at = range.end;
  }
  parts.push({ text: text.slice(at), accent: false });
  return { parts: parts.filter((part) => part.text.length > 0), used };
}

/** Plain text pieces with the accent words marked. React escapes each piece; no markup or regex. */
export function pageAccentParts(text: string, highlight?: string) {
  const { parts } = accentPieces(text, pageAccentPhrases(highlight, text));
  return parts.length ? parts : [{ text, accent: false }];
}

/**
 * Accent words in formatted text: each phrase's first appearance within a run of words, so a
 * word made bold or italic can still be accented. Game icons and links keep their shape.
 */
export function accentGuideInline(nodes: GuideInline[], highlight: string | undefined) {
  const words = (list: GuideInline[]): string =>
    list
      .map((node) =>
        node.type === "text" ? node.text : "children" in node ? words(node.children) : " ",
      )
      .join("");
  let remaining = pageAccentPhrases(highlight, words(nodes));
  const visit = (list: GuideInline[]): GuideInline[] =>
    list.flatMap((node): GuideInline[] => {
      if ("children" in node) return [{ ...node, children: visit(node.children) }];
      if (node.type !== "text" || !remaining.length) return [node];
      const { parts, used } = accentPieces(node.text, remaining);
      remaining = remaining.filter((phrase) => !used.includes(phrase));
      return parts.map((part) =>
        part.accent
          ? { type: "accent" as const, text: part.text }
          : { type: "text" as const, text: part.text },
      );
    });
  return remaining.length ? visit(nodes) : nodes;
}

/** Where a text's linked words sit in its source: each link's chosen occurrence of its words. */
export function pageTextLinkRanges(text: string, links: readonly PageTextLink[] = []) {
  const ranges: { start: number; end: number; link: PageTextLink }[] = [];
  for (const link of links) {
    let start = -1;
    for (let seen = 0; seen <= (link.occurrence ?? 0); seen++) {
      start = text.indexOf(link.text, start + 1);
      if (start < 0) break;
    }
    const end = start + link.text.length;
    // Words edited away, or already inside another link, are plain text again.
    if (start < 0 || ranges.some((range) => start < range.end && end > range.start)) continue;
    ranges.push({ start, end, link });
  }
  return ranges.sort((first, second) => first.start - second.start);
}

/**
 * A text's source split around its links, in order. Pieces stay plain strings for React to
 * escape; variables are resolved per piece, so a link never splits one.
 */
export function pageTextLinkParts(text: string, links?: readonly PageTextLink[]) {
  const parts: { text: string; href?: string }[] = [];
  let at = 0;
  for (const range of pageTextLinkRanges(text, links)) {
    if (range.start > at) parts.push({ text: text.slice(at, range.start) });
    parts.push({ text: text.slice(range.start, range.end), href: range.link.href });
    at = range.end;
  }
  if (at < text.length || !parts.length) parts.push({ text: text.slice(at) });
  return parts;
}

/** Which occurrence of `words` starts at `start` in `text`, counting from 0. */
export function pageTextOccurrence(text: string, words: string, start: number) {
  let count = 0;
  for (let at = text.indexOf(words); at >= 0 && at < start; at = text.indexOf(words, at + 1))
    count++;
  return count;
}

/**
 * The share of the site's width an element's column gets: a row divides its parent's share by
 * grow, a grid by its columns. Fixed-width siblings and feature visibility are ignored, so the
 * share errs large; it only caps fluid text (pageFluidTextFit).
 */
export function pageColumnShare(parent: PageNode | undefined, node: PageNode, share: number) {
  if (parent?.kind !== "frame" || !parent.children.some((child) => child.id === node.id))
    return share;
  if (parent.layout === "grid") return share / (parent.style?.columns ?? 2);
  if (parent.layout !== "row" || parent.style?.scroll === "horizontal") return share;
  const grow = (child: PageNode) => (child.style?.width ? 0 : (child.style?.grow ?? 1));
  const total = parent.children.reduce((sum, child) => sum + grow(child), 0);
  return total > 0 && grow(node) > 0 ? (share * grow(node)) / total : share;
}

/**
 * Fluid text keeps its longest word on one line above phone widths: its size stops at what
 * fits that word in its column's share of the site's width. Capitals in wide faces take about
 * 0.9em each and lowercase about 0.62em, plus the element's tracking, so the cap errs small.
 * Words still to be filled in ({{creator.name}}) aren't measured.
 */
export function pageFluidTextFit(
  text: string,
  style: PageNode["style"],
  share: number,
): Record<string, string> {
  const width = pageFluidWordWidth(text, style);
  if (!width) return {};
  const round = (value: number) => Math.round(value * 1000) / 1000;
  return {
    "--page-node-fit-size": `calc((min(100vw - 64px, var(--site-container-width, 1200px)) - ${fluidGaps(share)}px) * ${round(share)} / ${round(width)})`,
  };
}

/** The same cap in pixels, for a drawing of the page at a known width (theme thumbnails). */
export function pageFluidTextSize(
  text: string,
  style: PageNode["style"],
  share: number,
  pageWidth: number,
) {
  const width = pageFluidWordWidth(text, style);
  const size = style?.fontSize;
  return width && size
    ? Math.min(size, ((pageWidth - 64 - fluidGaps(share)) * share) / width)
    : size;
}

const fluidGaps = (share: number) => (share < 1 ? 48 : 0);

/** How many ems fluid text's longest word takes, or 0 when it isn't fluid or has no long word. */
function pageFluidWordWidth(text: string, style: PageNode["style"]) {
  if (!style?.fluidType || !style.fontSize) return 0;
  const words = text
    .replace(/\{\{[^}]*\}\}/g, " ")
    .replace(/[*_[\]()#>`~]/g, "")
    .split(/\s+/);
  const longest = Math.max(0, ...words.map((word) => [...word].length));
  if (longest < 4) return 0;
  const capitals = style.uppercase || text === text.toUpperCase();
  return longest * ((capitals ? 0.9 : 0.62) + (style.letterSpacing ?? 0));
}

/** The editor and SSR renderer share only checked token values and dimensions. */
export function pageNodeStyle(style: PageNode["style"]): Record<string, string> {
  const background = tokenOrColor(style?.background, backgrounds);
  const color = tokenOrColor(style?.color, colors) ?? regionInk[style?.background ?? ""];
  return {
    "--page-node-gradient-from": tokenOrColor(style?.gradientFrom, gradientColors) ?? "initial",
    "--page-node-gradient-via": tokenOrColor(style?.gradientVia, gradientColors) ?? "initial",
    "--page-node-gap": dimension(style?.gap, "24px"),
    "--page-node-padding": dimension(style?.padding, "0px"),
    "--page-node-padding-x": dimension(style?.paddingX, dimension(style?.padding, "0px")),
    "--page-node-padding-y": dimension(style?.paddingY, dimension(style?.padding, "0px")),
    ...(style?.paddingTop === undefined
      ? {}
      : { "--page-node-padding-top": `${style.paddingTop}px` }),
    ...(style?.paddingBottom === undefined
      ? {}
      : { "--page-node-padding-bottom": `${style.paddingBottom}px` }),
    ...(style?.rotate ? { "--page-node-rotate": `${style.rotate}deg` } : {}),
    ...(style?.opacity !== undefined ? { "--page-node-opacity": String(style.opacity / 100) } : {}),
    ...(style?.blur ? { "--page-node-blur": `${style.blur}px` } : {}),
    ...(style?.layer
      ? {
          "--page-node-layer-x": `${style.layer.x}%`,
          "--page-node-layer-y": `${style.layer.y}%`,
          "--page-node-layer-width": `${style.layer.width}%`,
        }
      : {}),
    ...(style?.marquee ? { "--page-node-marquee": `${style.marquee.seconds}s` } : {}),
    "--page-node-mobile-padding": dimension(style?.mobilePadding, "24px"),
    "--page-node-mobile-padding-x": dimension(
      style?.mobilePaddingX,
      dimension(style?.mobilePadding, "24px"),
    ),
    "--page-node-max-width": dimension(style?.maxWidth, "100%"),
    "--page-node-width": dimension(style?.width),
    // A sideways row that names its columns shows that many cards at once; the rest scroll in.
    "--page-node-scroll-basis":
      style?.scroll === "horizontal" && style.columns
        ? `calc((100% - ${(style.columns - 1) * (style.gap ?? 24)}px) / ${style.columns})`
        : undefined,
    "--page-node-height": dimension(style?.height),
    "--page-node-mobile-height": dimension(style?.mobileHeight, dimension(style?.height)),
    "--page-node-icon-color": tokenOrColor(style?.iconColor, colors) ?? "currentColor",
    "--page-node-min-height": dimension(style?.minHeight, "0px"),
    "--page-node-overlap-top": dimension(style?.overlapTop, "0px"),
    "--page-node-sticky-top": dimension(style?.stickyTop, "0px"),
    "--page-node-font-style": style?.fontStyle ?? "normal",
    ...(style?.cardColors?.length
      ? Object.fromEntries(
          Array.from({ length: 6 }, (_, index) => {
            const background = style.cardColors![index % style.cardColors!.length]!;
            return [
              [`--page-card-color-${index + 1}`, tokenOrColor(background, backgrounds)!],
              [`--page-card-ink-${index + 1}`, cardInk(background)],
            ];
          }).flat(),
        )
      : {}),
    // An authored button is 44px tall, as the site's .button is. Its label keeps the text's 1.6
    // line height (a 25.6px line at 16px on phones), so 6px above and below leaves room for a
    // border up to 3px wide without the button growing past 44px. With so little padding, a lower
    // minimum height of the button's own sets its height too.
    "--page-node-button-min-height": dimension(style?.minHeight, "44px"),
    "--page-node-button-padding-x": dimension(style?.paddingX ?? style?.padding, "24px"),
    "--page-node-button-padding-y": dimension(style?.paddingY ?? style?.padding, "6px"),
    "--page-node-font-size":
      style?.fluidType && style.fontSize
        ? `clamp(${Math.min(style.fontSize, 48)}px, 8vw, ${style.fontSize}px)`
        : dimension(style?.fontSize),
    "--page-node-mobile-font-size":
      style?.mobileFontSize !== undefined
        ? dimension(style.mobileFontSize)
        : style?.fontSize !== undefined && style.fontSize >= 32
          ? `clamp(32px, 10vw, ${Math.min(style.fontSize, 48)}px)`
          : dimension(style?.fontSize),
    "--page-node-background": background ?? "transparent",
    "--page-node-color": color ?? "inherit",
    // A button keeps its own fill and label color unless the element sets one; `initial` keeps a
    // button from taking its frame's.
    "--page-node-fill": background && style?.background !== "none" ? background : "initial",
    "--page-node-ink": color ?? "initial",
    "--page-node-align": style?.align ? alignments[style.align] : "stretch",
    "--page-node-text-align": style?.align ? textAlignments[style.align] : "left",
    "--page-node-columns": style?.columns === undefined ? "initial" : String(style.columns),
    "--page-node-mobile-columns":
      style?.mobileColumns === undefined ? "initial" : String(style.mobileColumns),
    "--page-node-font": pageFont(style?.font),
    "--page-node-font-weight":
      style?.fontWeight === undefined ? "inherit" : String(style.fontWeight),
    "--page-node-line-height":
      style?.lineHeight === undefined
        ? "var(--page-default-line-height, 1.6)"
        : String(style.lineHeight),
    "--page-node-tracking":
      style?.letterSpacing === undefined
        ? "var(--page-default-tracking, normal)"
        : `${style.letterSpacing}em`,
    // Unset case follows the page default, which only theme typography changes.
    "--page-node-transform":
      style?.uppercase === true
        ? "uppercase"
        : style?.uppercase === false
          ? "none"
          : "var(--page-default-transform, none)",
    "--page-node-radius": style?.corners
      ? [
          style.corners.topLeft,
          style.corners.topRight,
          style.corners.bottomRight,
          style.corners.bottomLeft,
        ]
          .map((value) => `${value}${style.corners!.unit}`)
          .join(" ")
      : typeof style?.radius === "string"
        ? corners[style.radius]
        : dimension(style?.radius),
    "--page-node-grow": String(style?.grow ?? 1),

    "--page-node-basis": dimension(style?.width, style?.grow === 0 ? "auto" : "0px"),
    "--page-node-justify":
      style?.justify === "between"
        ? "space-between"
        : style?.justify
          ? alignments[style.justify]
          : "flex-start",
    "--page-node-self-align": style?.selfAlign
      ? (alignments[style.selfAlign as keyof typeof alignments] ?? "stretch")
      : "auto",
    "--page-node-wrap": style?.wrap ? "wrap" : "nowrap",
    // `initial` keeps a nested element from inheriting a proportion and lets cards use theirs.
    "--page-node-aspect": style?.aspect ? style.aspect.replace(":", " / ") : "initial",
    "--page-node-image-fit": style?.imageFit ?? "cover",
    "--page-node-image-position": style?.imagePosition ?? "center",
    "--page-node-mobile-image-position":
      style?.mobileImagePosition ?? style?.imagePosition ?? "center",
    "--page-node-icon": pageIconMask(style?.icon),
    "--page-node-gradient-to": tokenOrColor(style?.gradientTo, gradientColors) ?? "initial",
    // Only a list's own row height: its cards' elements inherit it rather than resetting it.
    ...(style?.rowHeight ? { "--page-node-row-height": `${style.rowHeight}px` } : {}),
  };
}

/**
 * A frame that draws something itself (an icon, a fill, a line, a pattern or a set height) is
 * complete without children, so the editor doesn't ask for content inside it.
 */
export function frameDrawsItself(style: PageNode["style"]) {
  return (
    !!style &&
    (!!style.icon ||
      (!!style.background && style.background !== "none") ||
      (!!style.border && style.border !== "none") ||
      style.pattern === "grid" ||
      !!style.minHeight ||
      !!style.height)
  );
}

/** The same finite attributes select CSS treatments in React and Astro. */
export function pageNodeTreatments(style: PageNode["style"]) {
  return {
    "data-page-contained": style?.container,
    "data-page-overlap": style?.overlapTop ? "" : undefined,
    "data-page-font": style?.font,
    "data-page-sticky": style?.stickyTop !== undefined ? "" : undefined,
    "data-page-sticky-viewport":
      style?.stickyTop !== undefined && style.stickyViewport ? "" : undefined,
    "data-page-sticky-mobile":
      style?.stickyTop !== undefined && style.stickyOnMobile === false ? "false" : undefined,
    "data-page-scroll": style?.scroll,
    "data-page-reverse": style?.reverse ? "" : undefined,
    "data-page-card-colors": style?.cardColors?.length ? "" : undefined,
    "data-page-weight": style?.fontWeight,
    "data-page-self-align": style?.selfAlign,
    "data-page-border": style?.border,
    "data-page-border-tone":
      style?.border && style.border !== "none" ? style.borderTone : undefined,
    "data-page-border-width":
      style?.border && style.border !== "none" && style.borderWidth && style.borderWidth > 1
        ? style.borderWidth
        : undefined,
    "data-page-shadow": style?.shadow && style.shadow !== "none" ? style.shadow : undefined,
    "data-page-pattern": style?.pattern,
    "data-page-reveal": style?.reveal,
    "data-page-arrangement":
      style?.arrangement && style.arrangement !== "grid" ? style.arrangement : undefined,
    "data-page-text-effect": style?.textEffect,
    "data-page-accent-style": style?.accentStyle === "serif-italic" ? "serif-italic" : undefined,
    "data-page-marquee": style?.marquee ? (style.marquee.reverse ? "reverse" : "") : undefined,
    "data-page-behind": style?.behind ? "" : undefined,
    "data-page-blend": style?.blend,
    "data-page-fade-edges": style?.fadeEdges ? "" : undefined,
    "data-page-gradient-to":
      style?.textEffect === "accent-gradient" &&
      [style.gradientFrom, style.gradientVia, style.gradientTo].some((value) =>
        tokenOrColor(value, gradientColors),
      )
        ? ""
        : undefined,
    "data-page-link-style": style?.linkStyle,
    // A button whose label and icon sit at its two ends (a link-in-bio row).
    "data-page-justify": style?.linkStyle && style.justify === "between" ? "between" : undefined,
    "data-page-icon": style?.icon,
    "data-page-mobile-width": style?.mobileWidth,
    "data-page-case":
      style?.uppercase === undefined ? undefined : style.uppercase ? "upper" : "normal",
    // A paragraph styled as a heading takes that heading's theme defaults, keeping its level.
    "data-page-text-style": style?.textStyle,
  };
}

/**
 * A plan description written as short lines, one benefit each, reads as a list (without any
 * typed bullets). Anything else, such as a paragraph, stays as written.
 */
export function planBenefits(description: string) {
  const lines = description
    .split("\n")
    .map((line) => line.replace(/^\s*(?:[-•*]\s+)?/, "").trim())
    .filter(Boolean);
  return lines.length > 1 && lines.every((line) => line.length <= 120) ? lines : [];
}

/** A text list's items: one per written line, without any bullet or number typed before it. */
export function pageListItems(text: string) {
  return text
    .split("\n")
    .map((line) => line.replace(/^\s*(?:[-•*]|\d{1,3}[.)])\s+/, "").trim())
    .filter(Boolean);
}

/** A plan's price as a member reads it: whole amounts without cents ($12, not $12.00). */
export function planPrice(cents: number, currency = "usd") {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
}
